import React from 'react';
import { motion } from 'framer-motion';
import { HiXMark, HiDocumentText, HiCheckCircle, HiExclamationTriangle } from 'react-icons/hi2';

const AuditReportModal = ({ isOpen, onClose, submission, audits, loading }) => {
  if (!isOpen || !submission) return null;

  const formatDate = date => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getReadinessColor = level => {
    const colors = {
      Excellent: 'bg-green-100 text-green-800',
      Good: 'bg-blue-100 text-blue-800',
      Fair: 'bg-yellow-100 text-yellow-800',
      'Needs Improvement': 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800',
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b-2 border-gray-200 bg-gradient-to-r from-white to-gray-50 px-6 py-5">
          <div>
            <h2 className="text-2xl font-bold text-black">Audit Reports</h2>
            <p className="text-sm text-gray-600">
              {submission.title || 'Submission Audit Reports'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 hover:shadow-md"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              <span className="ml-3 text-gray-600">Loading audit reports...</span>
            </div>
          ) : audits.length === 0 ? (
            <div className="py-12 text-center">
              <HiDocumentText className="mx-auto mb-4 h-16 w-16 text-gray-300" />
              <h3 className="mb-2 text-xl font-semibold text-gray-700">No Audit Reports</h3>
              <p className="text-gray-600">
                Audit reports will appear here once they are generated for this submission.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {audits.map((audit, index) => (
                <motion.div
                  key={audit.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="rounded-xl border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 shadow-lg"
                >
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-3">
                        <div
                          className={`rounded-lg p-2 ${
                            audit.auditType === 'automatic' ? 'bg-blue-100' : 'bg-purple-100'
                          }`}
                        >
                          {audit.auditType === 'automatic' ? (
                            <HiCheckCircle className="h-5 w-5 text-blue-600" />
                          ) : (
                            <HiExclamationTriangle className="h-5 w-5 text-purple-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-black">
                            {audit.auditType === 'automatic'
                              ? 'Automatic Audit Report'
                              : 'Manual Audit Report'}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {formatDate(audit.auditDate || audit.createdAt)}
                          </p>
                        </div>
                      </div>
                      {audit.auditorName && audit.auditorName !== 'Automatic Audit System' && (
                        <p className="text-sm text-gray-600">
                          Audited by: {audit.auditorName} ({audit.auditorEmail})
                        </p>
                      )}
                    </div>
                    {audit.readinessLevel && (
                      <span
                        className={`inline-flex rounded-full px-4 py-2 text-sm font-semibold ${getReadinessColor(audit.readinessLevel)}`}
                      >
                        {audit.readinessLevel}
                      </span>
                    )}
                  </div>

                  {audit.completenessScore !== undefined && (
                    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900">
                          Completeness Score
                        </span>
                        <span className="text-2xl font-bold text-blue-700">
                          {audit.completenessScore}%
                        </span>
                      </div>
                      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-blue-200">
                        <div
                          className="h-full bg-blue-600 transition-all"
                          style={{ width: `${audit.completenessScore}%` }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-blue-700">
                        {audit.filledFields || 0} / {audit.totalFields || 0} fields completed
                      </p>
                    </div>
                  )}

                  {audit.reportSummary && (
                    <div className="mb-4">
                      <h4 className="mb-2 text-sm font-semibold text-gray-700">Report Summary</h4>
                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">
                          {audit.reportSummary}
                        </pre>
                      </div>
                    </div>
                  )}

                  {audit.comments && (
                    <div className="mb-4">
                      <h4 className="mb-2 text-sm font-semibold text-gray-700">Comments</h4>
                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <p className="whitespace-pre-wrap text-sm text-gray-700">
                          {audit.comments}
                        </p>
                      </div>
                    </div>
                  )}

                  {audit.checklistItems && audit.checklistItems.length > 0 && (
                    <div>
                      <h4 className="mb-2 text-sm font-semibold text-gray-700">Checklist Items</h4>
                      <div className="space-y-2">
                        {audit.checklistItems.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3"
                          >
                            {item.completed ? (
                              <HiCheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                            )}
                            <span className="flex-1 text-sm text-gray-700">{item.text}</span>
                            {item.weight && item.weight > 1 && (
                              <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                                Weight: {item.weight}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AuditReportModal;
